<template>
  <div class="step-content">
    <ul class="optional__navi">
      <li v-for="optionName in optionList"
        :key="optionName"
        :class="{on: optionName === currentOption}"
        @click="currentOption = optionName">
        {{ $t(`page.registration.optional.${optionName}.title`) }}
      </li>
    </ul>

    <!-- option 1 : report options -->
    <div v-show="currentOption === 'report'"
          class="option1 optional-content">

      <div class="option__pv-amount">
        <p class="label">{{ $t('page.registration.optional.report.pvLimit.label') }}</p>
        <ui-selectbox :value="internalPvLimit"
                      :items="pvLimitOptionList"
                      :width="170"
                      @input="onPVLimitChange($event)" />
        <p v-if="internalPvLimit === 0" class="custom-input--wrap">
          <input id="customPvLimit" type="number" min="1"
            v-model.number="customPvLimit"
            @blur="checkCustomPvLimit"> PV
        </p>
      </div>

      <div class="option__analysis-date">
        <p class="label">{{ $t('common.analysisPeriod') }}</p>
        <ul>
          <li class="timezone--wrap">
            <span class="input__label">{{ $t('common.timezone') }}</span>
            <timezone-selectbox class="timezone-select"
              :value="timezoneName"
              :width="340"
              :disabled="isEditMode"
              @change="$emit('onTimezoneChange', $event)"/>
          </li>
          <li>
            <span class="input__label">{{ $t('common.startDate') }}</span>
            <span v-if="disableStartDate">{{ (startDate || editApplyRegDate) | dateAndTime }}</span>
            <template v-else>
              <label for="startDateInstantly">
                <input id="startDateInstantly" type="radio" name="startDate" value="true" :checked="!startDate"
                  @change="onDateOptionChange('startDate', false)"><i class="icon icon--radio-off"></i>
                <span>{{ $t('common.instantlyStart') }}</span>
              </label>
              <label for="startDateSelect">
                <input id="startDateSelect" type="radio" name="startDate" value="false" :checked="startDate"
                  @change="onDateOptionChange('startDate', true)"><i class="icon icon--radio-off"></i>
                <span>{{ $t('common.selectDate') }}</span>
                <div v-if="startDate" class="datepicker--wrap data__start-date">
                  <input type="text" readonly
                    :value="startDate | dashDate"
                    @click.stop.prevent="toggleDatePicker('start')">
                  <i class="icon icon--calendar-input"></i>
                  <date-picker v-if="showStartDatePicker"
                    :isShowTimezone="true"
                    :timezone="timezoneStr"
                    :value="startDate"
                    :minDate="minStartDate"
                    :maxDate="maxStartDate"
                    @input="onCalendarDateChange('startDate', $event)"
                    @onCloseClick="showStartDatePicker = false"/>
                </div>
              </label>
            </template>
          </li>
          <li>
            <span class="input__label">{{ $t('common.endDate') }}</span>
            <label
              class="end-date-item" :class="{ disabled: disableEndDate }">
              <div v-if="endDate" class="datepicker--wrap data__end-date">
                <input type="text" readonly
                  :value="endDate | dashDate"
                  :disabled="disableEndDate"
                  @click.stop.prevent="toggleDatePicker('end')">
                <i class="icon icon--calendar-input"></i>
                <date-picker v-if="showEndDatePicker"
                  :isShowTimezone="true"
                  :timezone="timezoneStr"
                  :value="endDate"
                  :minDate="minEndDate"
                  @input="onCalendarDateChange('endDate', $event)"
                  @onCloseClick="showEndDatePicker = false"/>
              </div>
            </label>
          </li>
        </ul>
      </div>
    </div>

    <!-- option 2 : capture options -->
    <div v-show="currentOption === 'capture'"
          class="option2 optional-content">
      <div>
        <p class="desc" v-html="$t('page.registration.optional.capture.desc')"></p>
        <input type="text"
               :placeholder="$t('common.placeHolder.enterExceptionSelector')"
               :value="exceptionSelector"
               @focus="onSetPageAddFocusContent(3, 2)"
               @input="$emit('update:exceptionSelector', $event.target.value.trim())">
      </div>
    </div>
  </div>
</template>

<script>
import { mapMutations } from 'vuex'
import { format, parse, addDays, startOfDay, endOfDay } from 'date-fns'
import { triggerClickEvent, tzOffsetDate, dateFormatTimezone } from '@/libs/utils'
import UiSelectbox from '@/components/ui/UiSelectbox'
import DatePicker from '@/components/date-picker'
import TimezoneSelectbox from '@/components/ui/TimezoneSelectbox'
import { parseFromTimeZone } from 'date-fns-timezone'

const optionList = ['report', 'capture']
const DATE_FORAMT = 'YYYY-MM-DD HH:mm:ss'

export default {
  name: 'RegistrationStep3',
  components: {
    UiSelectbox,
    DatePicker,
    TimezoneSelectbox
  },
  props: [
    'isEditMode',
    'pvLimit',
    'startDate',
    'endDate',
    'exceptionSelector',
    'editApplyRegDate',
    'timezone',
    'timezoneName',
    'editApplyMeta',
    'editApplyState'
  ],
  data () {
    return {
      currentOption: optionList[0],
      internalPvLimit: null,
      customPvLimit: 3000,
      showStartDatePicker: false,
      showEndDatePicker: false,
      disableStartDate: false,
      disableEndDate: false
    }
  },
  computed: {
    selectedPVLimit () {
      return this.pvLimit
    },
    minStartDate () {
      return addDays(tzOffsetDate(new Date(), this.timezone), 1)
    },
    maxStartDate () {
      if (!this.endDate) {
        return null
      }
      return parse(this.endDate)
    },
    minEndDate () {
      if (!this.startDate) {
        return tzOffsetDate(new Date(), this.timezone)
      }
      return parse(this.startDate)
    },
    timezoneStr () {
      return `(${this.timezone}) ${this.timezoneName}`
    }
  },
  watch: {
    customPvLimit (value) {
      this.$emit('update:pvLimit', value)
    },
    startDate (value) {
      if (value && this.endDate && parse(value) > parse(this.endDate)) {
        this.$emit('update:endDate', format(addDays(this.startDate, 6), DATE_FORAMT))
      }
      this.onSetPageAddFocusContent(3, 1)
    },
    pvLimit () {
      this.onSetPageAddFocusContent(3, 0)
    },
    endDate () {
      this.onSetPageAddFocusContent(3, 1)
    },
    currentOption (value) {
      if (value && value === 'capture') {
        this.onSetPageAddFocusContent(3, 2)
      }
    }
  },
  created () {
    this.optionList = optionList
    this.pvLimitOptionList = [
      {
        value: 3000,
        text: '3,000'
      },
      {
        value: 5000,
        text: '5,000'
      },
      {
        value: 10000,
        text: '10,000'
      },
      {
        value: 20000,
        text: '20,000'
      },
      {
        value: 50000,
        text: '50,000'
      },
      {
        value: 0,
        text: this.$t('page.registration.optional.report.pvLimit.directInput')
      },
      {
        value: -1,
        text: this.$t('common.noLimit')
      }
    ]

    const pvItem = this.pvLimitOptionList.find(item => {
      return item.value !== 0 && item.value === this.pvLimit
    })

    const pvLimit = pvItem ? pvItem.value : 0
    this.internalPvLimit = pvLimit

    if (this.internalPvLimit === 0) {
      this.customPvLimit = this.pvLimit
    }

    if (this.isEditMode) {
      const now = new Date()
      this.disableStartDate = !this.editApplyMeta.canChangeStartDate || (this.editApplyState !== 'pending' && (!this.startDate || now >= parse(this.startDate)))
      this.disableEndDate = !this.editApplyMeta.canChangeEndDate || (this.endDate && now > parse(this.endDate))

      if (this.startDate.length > 10) {
        this.$emit('update:startDate', dateFormatTimezone(parse(this.startDate), this.timezoneName))
      }
      if (this.endDate.length > 10) {
        this.$emit('update:endDate', dateFormatTimezone(parse(this.endDate), this.timezoneName))
      }

    }
  },
  methods: {
    ...mapMutations([
      'setPageAddGuideContent'
    ]),
    onSetPageAddFocusContent (step, index) {
      const values = { currentStep: step, currentContentIndex: index }
      this.setPageAddGuideContent(values)
    },
    onPVLimitChange (value) {
      this.internalPvLimit = value
      const pvLimit = this.internalPvLimit === 0 ? this.customPvLimit : value
      this.$emit('update:pvLimit', pvLimit)
    },
    onDateOptionChange (dateType, value) {
      let dateValue = null

      if (value) {
        const now = tzOffsetDate(new Date(), this.timezone)

        if (dateType === 'startDate') {
          dateValue = addDays(now, 1)
        } else {
          if (this.startDate) {
            dateValue = endOfDay(addDays(parse(this.startDate), 6))
          } else {
            dateValue = endOfDay(addDays(now, 6))
          }
        }
        dateValue = format(dateValue, 'YYYY-MM-DD')
      }
      this.$emit(`update:${dateType}`, dateValue)
    },
    onCalendarDateChange (dateType, value) {
      this.$emit(`update:${dateType}`, value)
    },
    formatDate (value) {
      return format(parse(value), 'YYYY-MM-DD')
    },
    checkCustomPvLimit () {
      const value = Number(this.customPvLimit)

      if (!value || isNaN(value)) {
        this.customPvLimit = 3000
      }
    },
    toggleDatePicker (type) {
      switch (type) {
        case 'start':
          if (!this.showStartDatePicker) {
            triggerClickEvent()
          }
          this.showStartDatePicker = !this.showStartDatePicker
          break
        case 'end':
          if (!this.showEndDatePicker) {
            triggerClickEvent()
          }
          this.showEndDatePicker = !this.showEndDatePicker
          break
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.optional__navi {
  border-bottom: 1px solid #ebebeb;
  margin-top: 29px;
  padding-left: 35px;

  li {
    display: inline-block;
    min-width: 83px;
    height: 36px;
    padding: 11px 5px;
    box-sizing: border-box;
    text-align: center;

    font-size: 15px;
    font-weight: bold;
    line-height: 1;
    color: #999999;
    cursor: pointer;

    &.on {
      border-bottom: 3px solid $brandColor;
      color: #2f2f2f;
    }
    &:nth-last-of-type(1) {
      margin-left: 30px;
    }
  }
}

.option1 {
  padding-top: 50px;
  padding-left: 140px;

  .option__pv-amount {
    .custom-input--wrap {
      display: inline-block;
      margin-left: 5px;
      vertical-align: top;
      #customPvLimit {
        border-radius: 6px;
        border: solid 1px #bbbbbb;
        background-color: #ffffff;
        width: 140px;
        height: 38px;
        padding-left: 19px;
        padding-right: 12px;
        text-align: right;
        box-sizing: border-box;

        &:focus {
          border-color: #289eef;
        }
      }
    }
  }

  .option__analysis-date {
    margin-top: 50px;
    ul {
      margin-top: 0;
      padding-left: 6px;
      li {
        margin: 30px 0;

        .input__label {
          display: inline-block;
          width: 100px;
          vertical-align: middle;
        }

        label {
          position: relative;
          display: inline-block;
          white-space: nowrap;

          &:nth-of-type(1) {
            margin-right: 40px;
          }

          & > .icon:not(.icon--calendar-input){
            position: absolute;
            top: 50%;
            left: 0;
            transform: translateY(-50%);
          }

          & > span {
            display: inline-block;
            margin-left: 26px;
          }

          &.end-date-item {
            &.disabled {
              opacity: 0.5;
              pointer-events: none;
            }

            .datepicker--wrap {
              margin-left: 0 !important;
            }
          }
        }

        &.timezone--wrap {
          margin-top: 10px;
          margin-bottom: -10px;
          .timezone-select {
            vertical-align: middle;
          }
        }

        .datepicker--wrap {
          @include positionYcenter;
          display: inline-block;
          margin-left: 15px;

          & > .icon {
            position: absolute;
            top: 50%;
            left: 9px;
            transform: translateY(-50%);
          }

          & > input[type=text] {
            width: 129px;
            height: 38px;
            border-radius: 6px;
            background-color: #ffffff;
            border: 1px solid #bbbbbb;
            box-sizing: border-box;
            text-align: right;
            font-size: 14px;
            color: #2f2f2f;
            padding-right: 12px;

            &:focus {
              border-color: #289eef;
            }
          }

          .date_picker {
            top: -267px;
            left: 50%;
          }
        }
      }
    }
  }
}

.option2 {
  padding-top: 68px;
  padding-left: 70px;

  .desc {
    font-size: 13px;
    font-weight: bold;
    line-height: 1.54;
    color: #767676;
  }

  input {
    margin-top: 12px;
    width: 596px;
    height: 38px;
    border-radius: 6px;
    border: solid 1px #bbbbbb;
    padding-left: 12px;
    padding-right: 12px;
    box-sizing: border-box;
    background-color: #ffffff;
  }
}
html[lang="ja"] {
  .option1 .option__analysis-date ul li label {
    width: 100px;
  }
}
html[lang="en"] {
  .option1 .option__analysis-date ul li label {
    width: 125px;
  }
}
</style>
